
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, RotateCcw, Trash2, Send, Building2, User, Mail, CheckCircle, Clock, Calendar, Edit, FileCheck } from 'lucide-react';
import type { Application } from '@/types/database.types';
import { formatDistanceToNow, format } from 'date-fns';
import { useApplicationProgress } from '@/hooks/use-application-progress';

// Extended interface to include user profile information
interface ApplicationWithUser extends Application {
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

interface ApplicationCardProps {
  application: ApplicationWithUser;
  onView: (applicationId: string) => void;
  onResetRequest: (applicationId: string) => void;
  onDeleteRequest: (applicationId: string) => void;
  onSubmit: (applicationId: string) => void;
  isActionInProgress: boolean;
}

export const ApplicationCard = ({
  application,
  onView,
  onResetRequest,
  onDeleteRequest,
  onSubmit,
  isActionInProgress
}: ApplicationCardProps) => {
  const { calculateProgress } = useApplicationProgress(application);
  
  console.log('ApplicationCard: Rendering application:', application.id, {
    companyName: application.company_legal_name,
    status: application.status,
    progress: application.progress,
    calculatedProgress: calculateProgress(),
    userEmail: application.user_email
  });

  // Get the display progress based on application status
  const getDisplayProgress = (): number => {
    if (application.status === 'submitted') return 100;
    return calculateProgress();
  };

  // Format timestamps with error handling
  const formatTimestamp = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch (error) {
      console.warn('ApplicationCard: Error formatting timestamp:', dateString, error);
      return 'Invalid date';
    }
  };

  const formatRelativeTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.warn('ApplicationCard: Error formatting relative time:', dateString, error);
      return 'Unknown time';
    }
  };

  // Determine submitted timestamp
  const getSubmittedTimestamp = () => {
    if (application.status === 'submitted' && application.updated_at) {
      return application.updated_at;
    }
    return null;
  };

  const submittedAt = getSubmittedTimestamp();

  try {
    return (
      <Card key={application.id} className="glass-card w-full overflow-hidden flex flex-col group border-primary/10 hover:shadow-elevation-2 transition-all duration-300 hover:translate-y-[-2px]">
        <CardHeader className="p-3 bg-gradient-to-r from-gray-50 to-white border-b">
          <CardTitle className="text-base truncate group-hover:text-primary transition-colors flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary/70" />
            {application.company_legal_name || 
             (application.user_first_name && application.user_last_name 
               ? `${application.user_first_name} ${application.user_last_name}'s Application`
               : application.user_email 
                 ? `${application.user_email}'s Application`
                 : `Application ${application.id.slice(0, 8)}...`
             )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2 flex-grow">
          {/* Status and Progress */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-700 font-medium flex items-center gap-1.5">
              <CheckCircle className={`h-3.5 w-3.5 ${application.status === 'submitted' ? 'text-primary' : 'text-amber-500'}`} />
              Status:
            </span> 
            <span className={`text-xs ${application.status === 'submitted' ? 'text-primary font-medium' : 'text-amber-500 font-medium'} px-1.5 py-0.5 rounded bg-gray-50`}>
              {(application.status || 'draft').toUpperCase()}
            </span>
          </div>

          {/* User Account Information */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-700 font-medium flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-gray-500" />
              Account:
            </span> 
            <span className="text-xs">
              {application.user_first_name && application.user_last_name 
                ? `${application.user_first_name} ${application.user_last_name}`
                : 'Unknown User'
              }
            </span>
          </div>

          {/* User Email (from profile) */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-700 font-medium flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-gray-500" />
              User Email:
            </span> 
            <span className="text-xs truncate max-w-[180px]">{application.user_email || 'Unknown'}</span>
          </div>

          {/* Progress */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-700 font-medium flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-gray-500" />
              Progress:
            </span> 
            <div className="flex items-center gap-2">
              <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${getDisplayProgress()}%` }}
                ></div>
              </div>
              <span className="text-xs">{getDisplayProgress()}%</span>
            </div>
          </div>

          {/* Timestamps Section */}
          <div className="border-t pt-2 mt-2 space-y-1.5">
            {/* Created */}
            {application.created_at && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Created:
                </span>
                <div className="text-right">
                  <div className="text-xs text-gray-800">{formatTimestamp(application.created_at)}</div>
                  <div className="text-xs text-gray-500">{formatRelativeTime(application.created_at)}</div>
                </div>
              </div>
            )}

            {/* Last Modified */}
            {application.last_modified && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 flex items-center gap-1">
                  <Edit className="h-3 w-3" />
                  Modified:
                </span>
                <div className="text-right">
                  <div className="text-xs text-gray-800">{formatTimestamp(application.last_modified)}</div>
                  <div className="text-xs text-gray-500">{formatRelativeTime(application.last_modified)}</div>
                </div>
              </div>
            )}

            {/* Submitted */}
            {submittedAt && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 flex items-center gap-1">
                  <FileCheck className="h-3 w-3" />
                  Submitted:
                </span>
                <div className="text-right">
                  <div className="text-xs text-gray-800">{formatTimestamp(submittedAt)}</div>
                  <div className="text-xs text-gray-500">{formatRelativeTime(submittedAt)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Non-profit indicator */}
          {application.is_non_profit && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-700 font-medium">Type:</span> 
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">501(c)(3) Non-Profit</span>
            </div>
          )}
        </CardContent>
        <CardFooter className="p-3 border-t flex justify-between bg-gray-50/80">
          <div className="flex gap-1">
            <Button 
              variant="glass" 
              size="icon" 
              className="h-7 w-7" 
              onClick={(e) => {
                e.stopPropagation();
                onView(application.id);
              }}
              title="View Application"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            
            <Button 
              variant="glass" 
              size="icon" 
              className="h-7 w-7" 
              onClick={(e) => {
                e.stopPropagation();
                onResetRequest(application.id);
              }}
              title="Reset Progress"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
          
          <div className="flex gap-1">
            {application.status !== 'submitted' && (
              <Button 
                variant="glass-primary" 
                size="icon"
                className="h-7 w-7" 
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmit(application.id);
                }}
                disabled={isActionInProgress}
                title="Submit Application"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            )}
            
            <Button 
              variant="destructive" 
              size="icon" 
              className="h-7 w-7" 
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest(application.id);
              }}
              title="Delete Application"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardFooter>
      </Card>
    );
  } catch (error) {
    console.error('ApplicationCard: Error rendering application card:', application.id, error);
    return (
      <Card className="glass-card w-full overflow-hidden border-red-200">
        <CardContent className="p-4">
          <div className="text-red-600 text-sm">
            <p>Error displaying application: {application.id}</p>
            <p className="text-xs text-gray-500 mt-1">Check console for details</p>
          </div>
        </CardContent>
      </Card>
    );
  }
};
